import { defineStore } from 'pinia';
import DocNetAgentProfileService from 'src/services/docnet/DocNetAgentProfileService';
import AgentApiService from 'src/services/settings/AgentApiService';
import type { DocNetAgentProfile, DocNetAgentRole, DocNetDelegationStatus } from 'src/@types/docnet';
import { useDocNetSettingsStore } from './docnet-settings.store';
import { mockAgents } from 'src/mocks/docnet/agents';

const agentService = new DocNetAgentProfileService();
const agentApi = new AgentApiService();

export const useDocNetAgentStore = defineStore('docnet_agents', {
  state: () => ({
    profiles: [] as DocNetAgentProfile[],
    currentProfile: null as DocNetAgentProfile | null,
    loading: false,
    agentRuns: [] as any[],
    delegationStatus: [] as DocNetDelegationStatus[],
  }),

  getters: {
    activeAgents(state): DocNetAgentProfile[] {
      return state.profiles.filter(
        (p) => p.current_tasks.length > 0 || p.workload_percent > 0,
      );
    },

    agentsByRole:
      (state) =>
      (role: DocNetAgentRole): DocNetAgentProfile[] =>
        state.profiles.filter((p) => p.role === role),

    orgChartRoots(state): DocNetAgentProfile[] {
      return state.profiles.filter((p) => !p.reports_to);
    },

    getProfileById:
      (state) =>
      (id: string): DocNetAgentProfile | undefined =>
        state.profiles.find((p) => p.agent.id === id),

    agentNameMap(state): Record<string, string> {
      const map: Record<string, string> = {};
      for (const p of state.profiles) {
        map[p.agent.id] = p.agent.name;
      }
      return map;
    },

    agentSelectOptions(state): { label: string; value: string }[] {
      return state.profiles.map((p) => ({
        label: p.agent.name,
        value: p.agent.id,
      }));
    },

    getDelegationStatus:
      (state) =>
      (agentId: string): DocNetDelegationStatus | undefined =>
        state.delegationStatus.find((d) => d.agent_id === agentId),
  },

  actions: {
    async fetchProfiles(force = false): Promise<void> {
      const settings = useDocNetSettingsStore();
      if (settings.useMockData) { this.profiles = mockAgents; return; }
      if (!force && this.profiles.length > 0) return;
      this.loading = true;
      try {
        const profiles = await agentService.getProfiles();
        // Deduplicate by agent id (API may return duplicates)
        const seen = new Set<string>();
        this.profiles = profiles.filter((p) => {
          if (seen.has(p.agent.id)) return false;
          seen.add(p.agent.id);
          return true;
        });
      } catch (error) {
        console.error('[DocNet] Failed to fetch agent profiles:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async fetchProfile(id: string): Promise<void> {
      const settings = useDocNetSettingsStore();
      if (settings.useMockData) {
        this.currentProfile = mockAgents.find(p => p.agent.id === id) ?? null;
        return;
      }
      this.loading = true;
      try {
        const profile = await agentService.getProfileById(id);
        this.currentProfile = profile ?? null;

        if (profile) {
          const index = this.profiles.findIndex(
            (p) => p.agent.id === id,
          );
          if (index !== -1) {
            this.profiles[index] = profile;
          }
        }
      } catch (error) {
        console.error('[DocNet] Failed to fetch agent profile:', error);
        this.currentProfile = null;
      } finally {
        this.loading = false;
      }
    },

    async fetchAgentRuns(agentId: string): Promise<void> {
      try {
        this.agentRuns = await agentService.getAgentRuns(agentId);
      } catch (error) {
        console.error('[DocNet] Failed to fetch agent runs:', error);
        this.agentRuns = [];
      }
    },

    async fetchDelegationStatus(): Promise<void> {
      try {
        const status = await agentService.getDelegationStatus();
        this.delegationStatus = status;
      } catch (error) {
        console.error('[DocNet] Failed to fetch delegation status:', error);
      }
    },

    async updateAgentReportsTo(agentId: string, reportsTo: string | string[] | null): Promise<void> {
      try {
        // Use the dedicated per-org endpoint. Previously this went through
        // ``updateAgentConfig`` (the generic PUT /agents/{id} route) which,
        // for system agents, silently clobbered the globally-shared
        // ``agents.config`` row with the caller's per-org value — a
        // cross-org data leak. The dedicated endpoint correctly routes
        // system agents to ``agent_org_settings.custom_config`` and
        // leaves the shared row untouched.
        await agentApi.updateReportsTo(agentId, reportsTo);
        // Update local profile
        const idx = this.profiles.findIndex((p) => p.agent.id === agentId);
        if (idx !== -1) {
          this.profiles[idx].reports_to = reportsTo ?? undefined;
          if (this.profiles[idx].agent.config) {
            (this.profiles[idx].agent.config as any).reports_to = reportsTo;
          }
        }
      } catch (error) {
        console.error('[DocNet] Failed to update agent reports_to:', error);
        throw error;
      }
    },

    handleHeartbeatEvent(event: {
      agent_id: string;
      status: string;
      step: string;
      last_heartbeat_at: string;
    }): void {
      const idx = this.profiles.findIndex((p) => p.agent.id === event.agent_id);
      if (idx !== -1) {
        const profile = this.profiles[idx];
        profile.heartbeat_status = event.status as any;
        profile.heartbeat_step = event.step;
        profile.last_heartbeat_at = event.last_heartbeat_at;
        profile.last_active_at = event.last_heartbeat_at || profile.last_active_at;
        profile.workload_percent = event.status === 'working' ? 50 : 0;
      }
      if (
        this.currentProfile &&
        this.currentProfile.agent.id === event.agent_id
      ) {
        this.currentProfile.heartbeat_status = event.status as any;
        this.currentProfile.heartbeat_step = event.step;
        this.currentProfile.last_heartbeat_at = event.last_heartbeat_at;
        this.currentProfile.last_active_at =
          event.last_heartbeat_at || this.currentProfile.last_active_at;
        this.currentProfile.workload_percent = event.status === 'working' ? 50 : 0;
      }
    },
  },
});
